import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { customerService } from '../../../services/api';
import { parseApiFormErrors } from '../../../services/apiErrors';
import { useDepartments } from '../../../hooks/useDepartments';
import { validateFieldValue } from '../../../components/dashboard/companyFields';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import {
  EMAIL_DUPLICATE,
  accessSummary,
  capabilities,
  roleSummary,
  useAccessOptions,
  useCollaboratorsCapabilities,
} from './collaboratorStatus';

// Mirrors CollaboratorWriteSerializer (customer_service core/api/serializers/
// collaborator.py): Persona.full_name (255) and Persona.email (EmailField, 254).
const FIELD_RULES = {
  name: { required: true, maxLength: 255 },
  email: { required: true, maxLength: 254, format: 'email' },
};
const FORM_FIELDS = ['name', 'email', 'department', 'business_scope', 'global_role'];
const EDITABLE_EMAIL_STATUSES = ['pending', 'invited'];

let rowKey = 0;
const newMembership = (isPrimary) => ({ key: ++rowKey, business: '', role: '', is_primary: isPrimary });

// Client-side mirror of the access rules: all stores → a global role; specific
// stores → at least one row, each with a store (once) and a role.
const validateAccess = ({ scope, globalRole, memberships }) => {
  if (scope === 'all') return { globalRole: globalRole ? null : 'globalRoleRequired', rows: {}, list: null };
  const rows = {};
  memberships.forEach((membership, index) => {
    const errors = {};
    if (!membership.business) errors.business = 'storeRequired';
    else if (memberships.findIndex((m) => m.business === membership.business) !== index) {
      errors.business = 'storeDuplicate';
    }
    if (!membership.role) errors.role = 'storeRoleRequired';
    if (Object.keys(errors).length) rows[membership.key] = errors;
  });
  return { globalRole: null, rows, list: memberships.length === 0 ? 'storesRequired' : null };
};

export default function CollaboratorForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const collaboratorQuery = useQuery({
    queryKey: ['collaborator', id],
    queryFn: () => customerService.getCollaborator(id).then((res) => res.data),
    enabled: isEditMode,
  });
  const { stores, roles: activeRoles } = useAccessOptions();
  const collectionCapabilities = useCollaboratorsCapabilities();
  const { data: departments = [] } = useDepartments();
  const collaborator = collaboratorQuery.data;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [scope, setScope] = useState('limited');
  const [globalRole, setGlobalRole] = useState('');
  const [memberships, setMemberships] = useState(() => [newMembership(true)]);
  const [blurred, setBlurred] = useState({});
  const [touched, setTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [membershipErrors, setMembershipErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [flashMessage, setFlashMessage] = useState('');
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (!collaborator) return;
    setName(collaborator.name);
    setEmail(collaborator.email);
    setDepartment(collaborator.department?.uuid ?? '');
    setScope(collaborator.business_scope);
    setGlobalRole(collaborator.global_role?.uuid ?? '');
    setMemberships(
      collaborator.memberships.length
        ? collaborator.memberships.map((m) => ({
            key: ++rowKey,
            business: m.business.uuid,
            role: m.role.uuid,
            is_primary: m.is_primary,
          }))
        : [newMembership(true)],
    );
  }, [collaborator]);

  const isOwner = Boolean(collaborator?.is_owner);
  // The owner's access and your own can't change (the API refuses it too):
  // the access card turns read-only and the payload leaves it out.
  const accessLocked = isEditMode && capabilities(collaborator).can_edit_access === false;
  const emailEditable = !isEditMode || EDITABLE_EMAIL_STATUSES.includes(collaborator?.status);

  // An inactive role already assigned stays selectable, so editing never
  // silently blanks it.
  const roles = [
    ...activeRoles,
    ...[collaborator?.global_role, ...(collaborator?.memberships ?? []).map((m) => m.role)].filter(
      (role) => role && !activeRoles.some((active) => active.uuid === role.uuid),
    ),
  ].filter((role, index, all) => all.findIndex((r) => r.uuid === role.uuid) === index);

  const clientError = (field, value) => {
    if (field === 'email' && !emailEditable) return null;
    const error = validateFieldValue(value, FIELD_RULES[field]);
    return error ? t(`collaborators.validation.${error.key}`, error.params) : null;
  };
  const showClientError = (field) => touched || blurred[field];
  const fieldError = (field, value) =>
    (showClientError(field) && clientError(field, value)) || fieldErrors[field] || null;

  const access = accessLocked ? { globalRole: null, rows: {}, list: null } : validateAccess({ scope, globalRole, memberships });
  const accessValid = !access.globalRole && !access.list && Object.keys(access.rows).length === 0;
  const isValid = !clientError('name', name) && !clientError('email', email) && accessValid;

  const updateMembership = (key, patch) => {
    setMemberships((prev) => prev.map((m) => (m.key === key ? { ...m, ...patch } : m)));
    setMembershipErrors((prev) => ({ ...prev, [key]: undefined }));
  };
  const setPrimary = (key) => setMemberships((prev) => prev.map((m) => ({ ...m, is_primary: m.key === key })));
  const removeMembership = (key) =>
    setMemberships((prev) => {
      const next = prev.filter((m) => m.key !== key);
      if (next.length && !next.some((m) => m.is_primary)) next[0] = { ...next[0], is_primary: true };
      return next;
    });

  const resetFormFields = () => {
    setName('');
    setEmail('');
    setDepartment('');
    setScope('limited');
    setGlobalRole('');
    setMemberships([newMembership(true)]);
    setBlurred({});
    setTouched(false);
    nameInputRef.current?.focus();
  };

  const buildPayload = () => {
    const payload = { name: name.trim(), department: department || null };
    if (emailEditable) payload.email = email.trim();
    if (!accessLocked) {
      payload.business_scope = scope;
      if (scope === 'all') {
        payload.global_role = globalRole;
      } else {
        payload.memberships = memberships.map(({ business, role, is_primary: isPrimary }) => ({
          business,
          role,
          is_primary: isPrimary,
        }));
      }
    }
    return payload;
  };

  const mutation = useMutation({
    mutationFn: () =>
      isEditMode
        ? customerService.updateCollaborator(id, buildPayload())
        : customerService.createCollaborator(buildPayload()),
    // `intent` is whichever submit button was clicked ('another' only in create mode).
    onSuccess: (response, intent) => {
      queryClient.invalidateQueries({ queryKey: ['collaborators'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['collaborator', id] });
        navigate(`/collaborators/${response.data.uuid}`);
        return;
      }
      if (intent === 'another') {
        resetFormFields();
        setFlashMessage(t('collaborators.saveSuccessCreate'));
        return;
      }
      navigate('/collaborators', { state: { flashMessage: t('collaborators.saveSuccessCreate') } });
    },
    onError: (error) => {
      if (error?.response?.status === 403) {
        setFieldErrors({});
        setFormError(t('collaborators.saveForbidden'));
        return;
      }
      const data = error?.response?.data ?? {};
      const { memberships: apiMemberships, ...rest } = data;
      const { fieldErrors: apiFieldErrors, formError: apiFormError } = parseApiFormErrors(
        { response: { data: rest } },
        FORM_FIELDS,
      );
      if (apiFieldErrors.email === EMAIL_DUPLICATE) apiFieldErrors.email = t('collaborators.validation.emailDuplicate');
      setFieldErrors(apiFieldErrors);
      // Per-row errors come as a list aligned with the rows; a cross-row
      // error (duplicate store, two primaries) comes as plain messages.
      if (Array.isArray(apiMemberships) && apiMemberships.some((item) => typeof item === 'object')) {
        const rowErrors = {};
        apiMemberships.forEach((item, index) => {
          if (item && Object.keys(item).length) {
            rowErrors[memberships[index]?.key] = Object.fromEntries(
              Object.entries(item).map(([field, messages]) => [field, [].concat(messages).join(' ')]),
            );
          }
        });
        setMembershipErrors(rowErrors);
      }
      const listMessage =
        Array.isArray(apiMemberships) && apiMemberships.every((item) => typeof item === 'string')
          ? apiMemberships.join(' ')
          : null;
      const generalError = [apiFormError, listMessage].filter(Boolean).join(' ');
      const hasFieldErrors = Object.keys(apiFieldErrors).length > 0 || Boolean(apiMemberships);
      setFormError(generalError || (hasFieldErrors ? '' : t('collaborators.saveError')));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setFieldErrors({});
    setMembershipErrors({});
    setFormError('');
    if (!isValid) return;
    const intent = event.nativeEvent.submitter?.value || 'list';
    mutation.mutate(intent);
  };

  const header = (
    <PageHeader
      title={isEditMode ? t('collaborators.editTitle') : t('collaborators.createTitle')}
      breadcrumbs={[
        { label: t('collaborators.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('collaborators.breadcrumb.list'), to: '/collaborators' },
        { label: isEditMode ? t('collaborators.breadcrumb.edit') : t('collaborators.breadcrumb.new') },
      ]}
    />
  );

  if (isEditMode && collaboratorQuery.isLoading) {
    return (
      <BoundedPageLayout header={header}>
        <LoadingState message={t('collaborators.states.loading')} />
      </BoundedPageLayout>
    );
  }

  // Reached by URL without the permission: the API would refuse the save.
  // Undefined while the capabilities load, so nothing flashes meanwhile.
  const allowed = isEditMode ? capabilities(collaborator).can_edit : collectionCapabilities?.can_create;
  if (allowed === false) {
    return (
      <BoundedPageLayout header={header}>
        <PermissionDenied
          title={t('collaborators.states.permissionDeniedTitle')}
          message={t('collaborators.states.permissionDeniedEditMessage')}
        />
      </BoundedPageLayout>
    );
  }

  const nameError = fieldError('name', name);
  const emailError = fieldError('email', email);
  const showAccessErrors = touched;
  const rowError = (membership, field) => {
    const message = membershipErrors[membership.key]?.[field];
    if (message) return message;
    const key = showAccessErrors && access.rows[membership.key]?.[field];
    return key ? t(`collaborators.validation.${key}`) : null;
  };

  return (
    <BoundedPageLayout header={header}>
      <Stack component="form" onSubmit={handleSubmit} noValidate spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('collaborators.sections.identification')}
            subheader={t('collaborators.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  required
                  inputRef={nameInputRef}
                  label={t('collaborators.fields.name')}
                  value={name}
                  error={Boolean(nameError)}
                  helperText={nameError || t('collaborators.fields.nameHelper')}
                  onChange={(event) => {
                    setName(event.target.value);
                    setFieldErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  onBlur={() => setBlurred((prev) => ({ ...prev, name: true }))}
                  slotProps={{ htmlInput: { maxLength: FIELD_RULES.name.maxLength } }}
                  fullWidth
                />
                <TextField
                  required
                  type="email"
                  label={t('collaborators.fields.email')}
                  value={email}
                  disabled={!emailEditable}
                  error={Boolean(emailError)}
                  helperText={
                    emailError ||
                    (emailEditable ? t('collaborators.fields.emailHelper') : t('collaborators.fields.emailLocked'))
                  }
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  onBlur={() => setBlurred((prev) => ({ ...prev, email: true }))}
                  slotProps={{ htmlInput: { maxLength: FIELD_RULES.email.maxLength } }}
                  fullWidth
                />
              </Stack>
              <TextField
                select
                label={t('collaborators.fields.department')}
                value={department}
                error={Boolean(fieldErrors.department)}
                helperText={fieldErrors.department || t('collaborators.fields.departmentHelper')}
                onChange={(event) => {
                  setDepartment(event.target.value);
                  setFieldErrors((prev) => ({ ...prev, department: undefined }));
                }}
                fullWidth
              >
                <MenuItem value="">
                  <em>{t('collaborators.fields.noDepartment')}</em>
                </MenuItem>
                {departments.map((item) => (
                  <MenuItem key={item.uuid} value={item.uuid}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('collaborators.sections.access')}
            subheader={t('collaborators.sections.accessHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            {accessLocked ? (
              <Stack spacing={2}>
                <Alert severity="info">{t(isOwner ? 'collaborators.ownerLocked' : 'collaborators.selfLocked')}</Alert>
                <Typography variant="body2">
                  {accessSummary(t, collaborator)} · {roleSummary(t, collaborator)}
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                <FormControl error={Boolean(fieldErrors.business_scope)}>
                  <RadioGroup
                    value={scope}
                    onChange={(event) => {
                      setScope(event.target.value);
                      setFieldErrors((prev) => ({ ...prev, business_scope: undefined, global_role: undefined }));
                    }}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio />}
                      label={
                        <Stack>
                          <Typography variant="body1">{t('collaborators.scopes.all')}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('collaborators.scopeHelpers.all')}
                          </Typography>
                        </Stack>
                      }
                      sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.5 } }}
                    />
                    {/* Each option's fields sit right below it, aligned with its label. */}
                    {scope === 'all' && (
                      <Stack sx={{ pl: 4, pt: 1, pb: 2 }}>
                        <TextField
                          select
                          required
                          label={t('collaborators.fields.globalRole')}
                          value={globalRole}
                          error={Boolean(fieldErrors.global_role || (showAccessErrors && access.globalRole))}
                          helperText={
                            fieldErrors.global_role ||
                            (showAccessErrors && access.globalRole
                              ? t(`collaborators.validation.${access.globalRole}`)
                              : t('collaborators.fields.globalRoleHelper'))
                          }
                          onChange={(event) => {
                            setGlobalRole(event.target.value);
                            setFieldErrors((prev) => ({ ...prev, global_role: undefined }));
                          }}
                          fullWidth
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.uuid} value={role.uuid}>
                              {role.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    )}
                    <FormControlLabel
                      value="limited"
                      control={<Radio />}
                      label={
                        <Stack>
                          <Typography variant="body1">{t('collaborators.scopes.limited')}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('collaborators.scopeHelpers.limited')}
                          </Typography>
                        </Stack>
                      }
                      sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.5 } }}
                    />
                  </RadioGroup>
                  {fieldErrors.business_scope && <FormHelperText>{fieldErrors.business_scope}</FormHelperText>}
                </FormControl>

                {scope === 'limited' && (
                  <Stack spacing={2}>
                    <RadioGroup value={memberships.find((m) => m.is_primary)?.key ?? ''}>
                      <Stack spacing={2}>
                        {memberships.map((membership) => (
                          <Stack
                            key={membership.key}
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ sm: 'flex-start' }}
                          >
                            <TextField
                              select
                              required
                              label={t('collaborators.fields.store')}
                              value={membership.business}
                              error={Boolean(rowError(membership, 'business'))}
                              helperText={rowError(membership, 'business') || ' '}
                              onChange={(event) => updateMembership(membership.key, { business: event.target.value })}
                              sx={{ flex: 1 }}
                            >
                              {stores
                                .filter(
                                  (store) =>
                                    store.uuid === membership.business ||
                                    !memberships.some((m) => m.business === store.uuid),
                                )
                                .map((store) => (
                                  <MenuItem key={store.uuid} value={store.uuid}>
                                    {store.name}
                                  </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                              select
                              required
                              label={t('collaborators.fields.storeRole')}
                              value={membership.role}
                              error={Boolean(rowError(membership, 'role'))}
                              helperText={rowError(membership, 'role') || ' '}
                              onChange={(event) => updateMembership(membership.key, { role: event.target.value })}
                              sx={{ flex: 1 }}
                            >
                              {roles.map((role) => (
                                <MenuItem key={role.uuid} value={role.uuid}>
                                  {role.name}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Stack direction="row" alignItems="center" sx={{ minHeight: 56 }}>
                              <FormControlLabel
                                value={membership.key}
                                control={<Radio onChange={() => setPrimary(membership.key)} />}
                                label={t('collaborators.fields.primary')}
                              />
                              <Tooltip title={t('collaborators.memberships.remove')}>
                                <span>
                                  <IconButton
                                    aria-label={t('collaborators.memberships.remove')}
                                    onClick={() => removeMembership(membership.key)}
                                    disabled={memberships.length === 1}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    </RadioGroup>
                    {showAccessErrors && access.list && (
                      <FormHelperText error>{t(`collaborators.validation.${access.list}`)}</FormHelperText>
                    )}
                    <Stack direction="row">
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setMemberships((prev) => [...prev, newMembership(prev.length === 0)])}
                        disabled={memberships.length >= stores.length}
                      >
                        {t('collaborators.memberships.add')}
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            )}
            {formError && (
              <Alert severity="error" sx={{ mt: 2.5 }}>
                {formError}
              </Alert>
            )}
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            {/* Back to wherever the user came from (list or detail). */}
            <Button type="button" color="inherit" onClick={() => navigate(-1)}>
              {t('collaborators.actions.cancel')}
            </Button>
            {!isEditMode && (
              <Button type="submit" name="intent" value="another" variant="outlined" disabled={mutation.isPending}>
                {t('collaborators.actions.saveAndAddAnother')}
              </Button>
            )}
            <Button type="submit" name="intent" value="list" variant="contained" disabled={mutation.isPending}>
              {isEditMode ? t('collaborators.actions.saveEdit') : t('collaborators.actions.saveCreate')}
            </Button>
          </CardActions>
        </Card>
      </Stack>

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
